import React, { useState } from "react";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { useGetOrdersQuery } from "../store/api/adminApi";
import {
  useGetAgreementsQuery,
  useGetAgreementTemplatesQuery,
  useUploadAgreementTemplateMutation,
  useCreateAgreementMutation,
} from "../store/api/agreementsApi";
import AgreementEditor from "../components/AgreementEditor";
import { downloadAgreement } from "../lib/agreementDownloads";
import { useToast } from "../components/Toast";
const empty = {
  search: "",
  status: "",
  emailStatus: "",
  dateField: "createdAt",
  from: "",
  to: "",
};
export default function Agreements() {
  const token = useSelector((state) => state.auth.token),
    toast = useToast(),
    [params] = useSearchParams();
  const [filters, setFilters] = useState(empty),
    [page, setPage] = useState(1),
    [selectedBooking, setSelectedBooking] = useState(
      params.get("booking") || "",
    ),
    [bookingSearch, setBookingSearch] = useState(""),
    [editor, setEditor] = useState(null),
    [exporting, setExporting] = useState(false);
  const query = Object.fromEntries(
    Object.entries(filters).filter(([, value]) => value),
  );
  const { data, isLoading, error } = useGetAgreementsQuery(
    { ...query, page },
    { pollingInterval: 5000 },
  );
  const { data: templates } = useGetAgreementTemplatesQuery(),
    { data: orders } = useGetOrdersQuery();
  const [upload, { isLoading: uploading }] =
      useUploadAgreementTemplateMutation(),
    [create, { isLoading: creating }] = useCreateAgreementMutation();
  const current = templates?.data?.[0];
  const fail = (error) =>
    toast.error(
      error?.data?.error?.message ||
        error.message ||
        "Could not complete this action.",
    );
  const change = (key, value) => {
    setFilters({ ...filters, [key]: value });
    setPage(1);
  };
  const exportZip = async (all) => {
    setExporting(true);
    try {
      await downloadAgreement(
        `/export.zip${all ? "" : "?" + new URLSearchParams(query)}`,
        all ? "all-agreements.zip" : "filtered-agreements.zip",
        token,
      );
    } catch (error) {
      fail(error);
    } finally {
      setExporting(false);
    }
  };
  const uploadPdf = async (event) => {
    const input = event.target;
    const file = input.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Choose a PDF of at most 10 MB.");
      return;
    }
    const form = new FormData();
    form.append("pdf", file);
    try {
      await upload(form).unwrap();
      toast.success(
        "New agreement template uploaded. Existing agreements are unchanged.",
      );
    } catch (error) {
      fail(error);
    } finally {
      input.value = "";
    }
  };
  const bookings = (orders?.data || []).filter((order) =>
    `${order.bookingNumber} ${order.customer?.name || ""} ${order.customer?.email || ""}`
      .toLowerCase()
      .includes(bookingSearch.toLowerCase()),
  );
  return (
    <div className="p-6 lg:p-8 bg-gray-50 min-h-screen space-y-6 font-manrope-regular text-sm text-gray-700">
      <header>
        <h1 className="text-2xl font-manrope-bold">Agreements</h1>
        <p className="text-gray-500 text-sm mt-1">
          Vehicle condition, customer signatures and saved PDF records.
        </p>
      </header>
      <div className="grid lg:grid-cols-2 gap-5">
        <section className="bg-white rounded-2xl p-5 border space-y-3">
          <h2 className="font-manrope-bold text-lg">Agreement template</h2>
          {current ? (
            <>
              <p>
                {current.name} · {current.pageCount} pages
              </p>
              <p className="text-xs text-gray-500">
                Version {current.id.slice(0, 8)} · Uploaded{" "}
                {new Date(current.createdAt).toLocaleString()}
              </p>
              <button
                onClick={() =>
                  downloadAgreement(
                    `/templates/${current.id}/pdf`,
                    "agreement-template.pdf",
                    token,
                  ).catch(fail)
                }
                className="text-primary underline text-sm text-[13px] font-manrope-semibold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 hover:bg-gray-50"
              >
                Download current template
              </button>
            </>
          ) : (
            <p className="text-sm text-gray-500">
              Upload the PDF containing your agreement and vehicle diagram.
            </p>
          )}
          <label className="block">
            <span className="text-sm font-manrope-bold">
              {current ? "Replace template" : "Upload template"}
            </span>
            <input
              aria-label="Agreement PDF template"
              type="file"
              accept="application/pdf,.pdf"
              disabled={uploading}
              onChange={uploadPdf}
              className="block mt-2 text-sm w-full file:mr-3 file:rounded-xl file:border-0 file:bg-primary file:px-4 file:py-2.5 file:text-white file:font-manrope-semibold file:cursor-pointer"
            />
          </label>
          <p className="text-xs text-gray-500">
            PDF only · 10 MB maximum · 30 pages. New uploads create a new
            version; existing drafts and signed records keep their original
            template.
          </p>
          {uploading && <p role="status">Validating and saving PDF…</p>}
        </section>
        <section className="bg-white rounded-2xl p-5 border space-y-3">
          <h2 className="font-manrope-bold text-lg">
            Open a customer agreement
          </h2>
          <label className="block text-sm">
            Find booking
            <input
              aria-label="Find agreement booking"
              value={bookingSearch}
              onChange={(event) => setBookingSearch(event.target.value)}
              placeholder="Customer name, email or booking number"
              className="border rounded-lg p-2 w-full mt-1"
            />
          </label>
          <label className="block text-sm">
            Booking
            <select
              aria-label="Agreement booking"
              value={selectedBooking}
              onChange={(event) => setSelectedBooking(event.target.value)}
              className="border rounded-lg p-2 w-full mt-1"
            >
              <option value="">Select a booking</option>
              {selectedBooking &&
                !bookings.some((order) => order.id === selectedBooking) && (
                  <option value={selectedBooking}>Selected booking</option>
                )}
              {bookings.map((order) => (
                <option key={order.id} value={order.id}>
                  {order.bookingNumber} · {order.customer?.name || "Customer"} ·{" "}
                  {order.date}
                </option>
              ))}
            </select>
          </label>
          <button
            disabled={!current || !selectedBooking || creating}
            onClick={async () => {
              try {
                const result = await create(selectedBooking).unwrap();
                setEditor(result.data.id);
              } catch (error) {
                fail(error);
              }
            }}
            className="bg-primary text-white px-4 py-2.5 rounded-xl disabled:opacity-40 text-[13px] font-manrope-semibold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 hover:opacity-90"
          >
            {creating ? "Opening…" : "Open agreement"}
          </button>
          <p className="text-xs text-gray-500">
            Hand the device to the customer to read, mark vehicle damage and
            sign. A signed agreement is emailed automatically.
          </p>
        </section>
      </div>
      <section className="bg-white rounded-2xl border p-5 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-manrope-bold text-lg">Agreement records</h2>
          <div className="flex flex-wrap gap-2">
            <button
              disabled={exporting}
              onClick={() => exportZip(false)}
              className="bg-primary text-white rounded-xl px-4 py-2.5 disabled:opacity-40 text-[13px] font-manrope-semibold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 hover:opacity-90"
            >
              {exporting ? "Preparing download…" : "Download filtered ZIP"}
            </button>
            <button
              disabled={exporting}
              onClick={() => exportZip(true)}
              className="border rounded-xl px-4 py-2.5 text-[13px] font-manrope-semibold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 hover:bg-gray-50 border-gray-200"
            >
              Download all ZIP
            </button>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <label className="text-sm">
            Customer / booking
            <input
              aria-label="Search agreement records"
              className="block w-full border p-2 rounded-lg mt-1"
              value={filters.search}
              onChange={(event) => change("search", event.target.value)}
              placeholder="Name, email, booking number"
            />
          </label>
          <label className="text-sm">
            Agreement status
            <select
              aria-label="Agreement status"
              value={filters.status}
              onChange={(event) => change("status", event.target.value)}
              className="block w-full border p-2 rounded-lg mt-1"
            >
              <option value="">All statuses</option>
              <option value="draft">Draft</option>
              <option value="signing">Finalizing</option>
              <option value="signed">Signed</option>
            </select>
          </label>
          <label className="text-sm">
            Email status
            <select
              aria-label="Agreement email status"
              value={filters.emailStatus}
              onChange={(event) => change("emailStatus", event.target.value)}
              className="block w-full border p-2 rounded-lg mt-1"
            >
              <option value="">All email statuses</option>
              {["not_sent", "pending", "sending", "sent", "failed"].map(
                (status) => (
                  <option key={status} value={status}>
                    {status.replace(/_/g, " ")}
                  </option>
                ),
              )}
            </select>
          </label>
          <label className="text-sm">
            Date range applies to
            <select
              aria-label="Agreement date field"
              value={filters.dateField}
              onChange={(event) => change("dateField", event.target.value)}
              className="block w-full border p-2 rounded-lg mt-1"
            >
              <option value="createdAt">Created date</option>
              <option value="signedAt">Signed date</option>
              <option value="booking.date">Trip date</option>
            </select>
          </label>
          <label className="text-sm">
            From
            <input
              aria-label="Agreement from date"
              type="date"
              value={filters.from}
              onChange={(event) => change("from", event.target.value)}
              className="block w-full border p-2 rounded-lg mt-1"
            />
          </label>
          <label className="text-sm">
            To
            <input
              aria-label="Agreement to date"
              type="date"
              value={filters.to}
              onChange={(event) => change("to", event.target.value)}
              className="block w-full border p-2 rounded-lg mt-1"
            />
          </label>
        </div>
        <div className="flex gap-4 text-xs text-gray-500">
          <span>
            Dates use Bonaire time (UTC−4), including the whole selected end
            date.
          </span>
          <button
            onClick={() => {
              setFilters(empty);
              setPage(1);
            }}
            className="underline text-[13px] font-manrope-semibold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 hover:bg-gray-50"
          >
            Reset filters
          </button>
        </div>
        <p className="text-xs text-gray-500">
          ZIP exports contain the PDFs and a manifest with booking details and
          file checksums. Draft PDFs are clearly labelled unsigned.
        </p>
        {isLoading && <p role="status">Loading agreements…</p>}
        {!!error && (
          <p role="alert" className="text-red-700">
            {error?.data?.error?.message || "Could not load agreements."}
          </p>
        )}
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead>
              <tr className="border-b text-gray-500">
                {[
                  "Booking / customer",
                  "Trip date",
                  "Status",
                  "Email",
                  "Created / signed",
                  "",
                ].map((title, index) => (
                  <th key={index} className="p-3 font-manrope-semibold">
                    {title}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data?.data?.map((record) => (
                <tr key={record.id} className="border-b">
                  <td className="p-3">
                    <p className="font-manrope-bold">
                      {record.booking.bookingNumber} ·{" "}
                      {record.booking.customerName}
                    </p>
                    <p className="text-xs text-gray-500">
                      {record.booking.customerEmail}
                    </p>
                  </td>
                  <td className="p-3 font-manrope-semibold">
                    {record.booking.date}
                    <br />
                    {record.booking.timeSlot}
                  </td>
                  <td className="p-3 capitalize">{record.status}</td>
                  <td className="p-3">
                    <span
                      className={
                        record.emailStatus === "failed" ? "text-red-700" : ""
                      }
                    >
                      {record.emailStatus.replace(/_/g, " ")}
                    </span>
                    {record.emailStatus === "failed" && (
                      <p className="text-xs">
                        {record.emailAttempts >= 6
                          ? "Open to resend"
                          : "Retry scheduled"}
                      </p>
                    )}
                  </td>
                  <td className="p-3 text-xs">
                    {new Date(record.createdAt).toLocaleString()}
                    {record.signedAt && (
                      <p>
                        Signed: {new Date(record.signedAt).toLocaleString()}
                      </p>
                    )}
                  </td>
                  <td className="p-3">
                    <button
                      onClick={() => setEditor(record.id)}
                      className="text-primary font-manrope-bold underline text-[13px] font-manrope-semibold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 hover:bg-gray-50"
                    >
                      {record.status === "signed"
                        ? "View / resend"
                        : "Read, mark & sign"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!isLoading && !error && !data?.data?.length && (
          <p className="text-gray-500 py-4">
            No agreements match these filters.
          </p>
        )}
        <div className="flex justify-between items-center text-sm">
          <span>
            {data?.meta?.total || 0} agreements · Page {page}
          </span>
          <div className="flex gap-3">
            <button
              disabled={page <= 1}
              onClick={() => setPage(page - 1)}
              className="border rounded-xl px-3 py-2.5 disabled:opacity-30 text-[13px] font-manrope-semibold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 hover:bg-gray-50 border-gray-200"
            >
              Previous
            </button>
            <button
              disabled={page * 25 >= (data?.meta?.total || 0)}
              onClick={() => setPage(page + 1)}
              className="border rounded-xl px-3 py-2.5 disabled:opacity-30 text-[13px] font-manrope-semibold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 hover:bg-gray-50 border-gray-200"
            >
              Next
            </button>
          </div>
        </div>
      </section>
      <p className="text-xs text-gray-500">
        Documents currently use private server storage. Keep independent backups
        of this storage and the database until S3 storage and backups are
        configured.
      </p>
      {editor && (
        <AgreementEditor
          key={editor}
          id={editor}
          onClose={() => setEditor(null)}
        />
      )}
    </div>
  );
}
